(ns app.front.view.dispatch.dispatch-map
  {:clj-kondo/config '{:linters {:refer                        {:level :off}
                                 ;; kondo and cljfmt disagree on sorting here
                                 :unsorted-required-namespaces {:level :off}}}}
  (:require ["@cloudpermit/map-component/dist/components/layer-tools/GLLayer" :refer (makeGLLayer)]
            ["@cloudpermit/map-component/dist/components/map/map-functions/MapFunctions" :as MapFunctions]
            ["ol/Collection" :default Collection]
            ["ol/extent" :refer (getTopLeft getWidth)]
            ["ol/Feature" :default Feature]
            ["ol/format/GeoJSON" :default GeoJSON]
            ["ol/geom/LineString" :default LineString]
            ["ol/geom/Point" :default Point]
            ["ol/interaction" :rename {defaults default-interactions}]
            ["ol/interaction/Select" :default Select]
            ["ol/layer/Group" :default LayerGroup]
            ["ol/layer/Tile" :default TileLayer]
            ["ol/layer/Vector" :default VectorLayer]
            ["ol/Map" :default Map]
            ["ol/proj" :as ol.proj]
            ["ol/source/TileArcGISRest" :default TileArcGISRest]
            ["ol/source/Vector" :default VectorSource]
            ["ol/source/WMTS" :default WMTS]
            ["ol/source/XYZ" :default XYZ]
            ["ol/style" :refer (Circle Icon Style Stroke Fill Text)]
            ["ol/tilegrid/WMTS" :default WMTSTileGrid]
            ["ol/View" :default View]
            [app.domain.booking.model :as booking-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.municipality :as municipality]
            [app.front.util :as futil]
            [app.front.view.dispatch.subs :as dispatch-subs]
            [app.front.view.group.admin.inspections.working-areas :as working-areas]
            [app.front.view.map-common.util :as map-common-util]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-sub ::area-feature-collection-with-selected
  :<- [::dispatch-subs/working-areas-feature-collection]
  :<- [::dispatch-subs/selected-inspector]
  (fn [[feature-collection inspector] _]
    (if-let [areas (some->> inspector :primary-areas seq (map :id) set)]
      (update feature-collection
              :features
              #(map (fn [{:keys [id] :as feat}]
                      (if (contains? areas id)
                        (assoc-in feat [:properties :selected] true)
                        feat))
                    %))
      feature-collection)))


(def style-cache (atom {}))


(def highlight-color "rgb(247,23,53)")


(def ol-view (View. #js {:zoom    16
                         :maxZoom 22}))


(defn marker-style [selected? initials]
  {:image (Circle. #js {:radius 18
                        :stroke (Stroke. #js {:color (if selected? highlight-color "#000000") :width 4})
                        :fill   (Fill. #js {:color "#000000"})})
   :text  (Text. #js {:text    initials
                      :offsetY 1
                      :font    "16px Inter, sans-serif"
                      :fill    (Fill. #js {:color "#ffffff"})})})


(defn working-area-style [^js feature]
  (let [props     (.getProperties feature)
        selected? (futil/oget props "selected")
        label     (futil/oget props "label")
        colour    (or (futil/oget props "colour")
                      (first working-areas/available-colours))]
    (Style. #js {:fill   (Fill. #js {:color (if selected?
                                              (str "#AFCCEF" working-areas/opacity)
                                              (str colour working-areas/opacity))})
                 :stroke (Stroke. #js {:color "#000000"
                                       :width 2})
                 :text   (Text. #js {:text label
                                     :font "bold 16px Inter, sans-serif"
                                     :fill (Fill. #js {:color "#000000"})})})))


(defn get-style [selected? ^js feature]
  (let [initials  (.get feature "name")
        cache-key (str initials selected?)]
    (or (get @style-cache cache-key)
        (-> (->> (Style. (clj->js (marker-style selected? initials)))
                 (swap! style-cache assoc cache-key))
            (get cache-key)))))


(defn get-route-style [^js feature]
  (let [resolution (-> ol-view .getResolution)]
    (->> feature
         .getGeometry
         .getCoordinates
         js->clj
         (partition 2 1)
         (map (fn [[[x1 y1] [x2 y2]]]
                (let [dx           (- x2 x1)
                      dy           (- y2 y1)
                      theta        (js/Math.atan2 dy dx) ; radian angle of the vector
                      pixels       30
                      r            (* resolution pixels) ; move arrow back from end because of the circle marker there
                      arrow-coords [(- x2 (* r (js/Math.cos theta))) (- y2 (* r (js/Math.sin theta)))]]
                  (Style. #js {:geometry (Point. (clj->js arrow-coords))
                               :image    (Icon. #js {:src            (i/icon->img-svg-src i/chevron-right
                                                                                          highlight-color)
                                                     :scale          2
                                                     :anchor         #js [0.75 0.5]
                                                     :rotateWithView true
                                                     :rotation       (- theta)})}))))
         (concat [(Style. #js {:stroke (Stroke. #js {:color highlight-color
                                                     :width 4})})])
         clj->js)))

(defn- booking-coordinates [booking]
  (-> (or (-> (booking-model/get-workspace booking)
              workspace-model/single-property
              :entity/location)
          (-> (booking-model/get-workspace booking)
              :entity/location))
      ((juxt :location.center/x :location.center/y))))

(defn booking->feature [booking]
  (let [coords (clj->js (booking-coordinates booking))]
    (Feature. #js {:geometry (Point. coords)
                   :name     (if-let [assignee (:booking/assignee booking)]
                               (user-model/initials assignee)
                               "")
                   :assignee (-> booking :booking/assignee :db/id)
                   :booking  (:db/id booking)})))


(defn bookings->linestring-features [bookings ids]
  (let [coords (->> bookings
                    (filter #(contains? (set ids) (:db/id %)))
                    (map booking-coordinates))]
    [(Feature. #js {:geometry (LineString. (clj->js coords) "XY")})]))


(defn ->vector-source [features]
  (VectorSource. #js {:features (Collection. (clj->js features))}))


(defn ->geojson-source [geojson-object]
  (VectorSource. #js {:features (if (-> geojson-object :features seq)
                                  (.readFeatures (GeoJSON.)
                                                 (clj->js geojson-object)
                                                 #js {:dataProjection    "EPSG:4326"
                                                      :featureProjection "EPSG:3857"})
                                  #js [])}))


(defn select-booking [^js event]
  (let [feature      (some-> event
                             .-selected
                             first)
        inspector-id (some-> feature
                             (.get "assignee"))
        booking-id   (some-> feature
                             (.get "booking"))]
    (>evt [::dispatch-subs/select-inspector-id inspector-id])
    (>evt [::dispatch-subs/set-selected-booking booking-id false])))


(def selected-bookings (Collection.))


(def booking-layer
  (VectorLayer. #js {:source nil
                     :style  (partial get-style false)}))


(def route-layer
  (VectorLayer. #js {:source nil
                     :style  get-route-style}))


(def working-area-layer
  (VectorLayer. #js {:source nil
                     :style  working-area-style}))


(defn set-selected-bookings [features selected-ids]
  (.clear selected-bookings)
  (->> features
       (filter #(contains? (set selected-ids) (.get % "booking")))
       clj->js
       (.extend selected-bookings)))

(def arcgis-basemap-url "/api/command/gis/basemap-style?type=Navigation")

(defn create-gl-layer [center target-id]
  (let [tr (<sub [:tr])]
    (makeGLLayer #js {:id             "osm"
                      :title          (tr [:map/basemap])
                      :opacity        1.0
                      :type           "mapbox-gl-basemap"
                      :mapboxStyleUrl arcgis-basemap-url}
                 target-id
                 (clj->js center))))

(defn tile-arcgis-rest
  ([url layers]
   (tile-arcgis-rest url layers {}))
  ([url layers params]
   (TileLayer. (clj->js (p/assoc-if {:source (TileArcGISRest.
                                               (clj->js {:url    url
                                                         :params (merge {:LAYERS (str "show:" layers)}
                                                                        (select-keys params [:token]))}))}
                                    :extent (:extent params))))))

(defn tile-xyz
  ([url]
   (TileLayer. (clj->js {:source (XYZ. (clj->js {:url url}))}))))

(def epsg-3857-projection (ol.proj/get "EPSG:3857"))
(def epsg-3857-full-extent (.getExtent epsg-3857-projection))
(def epsg-3857-origin (getTopLeft epsg-3857-full-extent))
(def epsg-3857-max-number-of-tiles (/ (getWidth epsg-3857-full-extent) 256))
(def wmts-zoom-levels 20)
(def wmts-matrix-ids (range wmts-zoom-levels))
(def wmts-resolutions (for [i wmts-matrix-ids]
                        (/ epsg-3857-max-number-of-tiles (js/Math.pow 2 i))))

(defn tile-wmts
  "Makes a TileLayer using a WMTS source.
  Currently always assumes EPSG:3857 with full extent, REST mode, 256 px tiles and 20 zoom levels."
  [url]
  (TileLayer. (clj->js
                {:source (WMTS. (clj->js
                                  {:url             url
                                   :matrixSet       "EPSG:3857"
                                   :projection      epsg-3857-projection
                                   :requestEncoding "REST"
                                   :tileGrid        (WMTSTileGrid. (clj->js {:origin      epsg-3857-origin
                                                                             :resolutions wmts-resolutions
                                                                             :matrixIds   wmts-matrix-ids}))}))})))

(defn tile-wms
  "Makes a TileLayer using a WMS source. Assumes EPSG:3857 projection and WMS version 1.3.0.
   WMS service must support tiling."
  [{:keys [url layers]}]
  (TileLayer. #js {:source (MapFunctions/createWMSTileSource url
                                                             (clj->js layers)
                                                             epsg-3857-projection
                                                             "1.3.0")}))

(defn make-layer [layer-config]
  (case (:type layer-config)
    :arcgis-rest (tile-arcgis-rest (:url layer-config)
                                   (str/join "," (:layers layer-config))
                                   layer-config)
    :xyz (tile-xyz (:url layer-config))
    :wmts-rest (tile-wmts (:url layer-config))
    :tile-wms (tile-wms layer-config)
    (js/console.warn (str "layer type " (:type layer-config) " not supported"))))

(defn update-custom-tile-layers [*map-atom layers vector-layers]
  (when (and layers
             (not= (:layers @*map-atom) layers))
    (let [^js map-object (:map @*map-atom)]
      (->> (LayerGroup. #js {:layers (->> (concat [(:gl-layer @*map-atom)]
                                                  (-> (keep make-layer layers)
                                                      ;; to match order in map-component
                                                      reverse)
                                                  vector-layers)
                                          clj->js)})
           (.setLayerGroup map-object))
      (swap! *map-atom assoc :layers layers))))

(defn ol-map [{:keys [id class style]}]
  (let [*map-atom  (r/atom nil)
        did-update (fn [component]
                     (let [{:keys [layers bookings selected-ids route-ids working-areas]} (r/props component)
                           ^js map-object                                                 (:map @*map-atom)]

                       (update-custom-tile-layers *map-atom layers [working-area-layer
                                                                    route-layer
                                                                    booking-layer])

                       (.updateSize map-object)

                       (when (or (not= bookings (:bookings @*map-atom))
                                 (not= selected-ids (:selected-ids @*map-atom)))
                         (let [features   (map booking->feature bookings)
                               new-source (->vector-source features)]
                           (.setSource booking-layer new-source)
                           (when (and (pos? (count bookings))
                                      (pos? (count selected-ids)))
                             (map-common-util/fit-map new-source ol-view))
                           (set-selected-bookings features selected-ids)))

                       (when (not= route-ids (:route-ids @*map-atom))
                         (->> (bookings->linestring-features bookings route-ids)
                              ->vector-source
                              (.setSource route-layer)))

                       (when (not= working-areas (:working-areas @*map-atom))
                         (->> (->geojson-source working-areas)
                              (.setSource working-area-layer)))

                       (swap! *map-atom merge {:bookings      bookings
                                               :route-ids     route-ids
                                               :selected-ids  selected-ids
                                               :working-areas working-areas})))]
    (r/create-class
      {:display-name         (str "ol-map-" id)
       :component-did-mount  (fn [component]
                               (let [{:keys [layers center bookings selected-ids
                                             route-ids working-areas]}           (r/props component)
                                     features                                    (map booking->feature bookings)
                                     booking-src                                 (->vector-source features)
                                     _                                           (.setSource booking-layer booking-src)
                                     _                                           (->> (bookings->linestring-features bookings route-ids)
                                                                                      ->vector-source
                                                                                      (.setSource route-layer))
                                     _                                           (->> (->geojson-source working-areas)
                                                                                      (.setSource working-area-layer))
                                     _                                           (set-selected-bookings features selected-ids)
                                     select-int                                  (doto (Select. #js {:multi    false
                                                                                                     :layers   #js [booking-layer]
                                                                                                     :style    (partial get-style true)
                                                                                                     :features selected-bookings})
                                                                                   (.on "select" select-booking))
                                     interactions                                (doto (default-interactions)
                                                                                   (.push select-int))
                                     gl-layer                                    (create-gl-layer center id)
                                     ol-map                                      (Map. #js {:target       id
                                                                                            :layers       (->> (concat [gl-layer]
                                                                                                                       (-> (keep make-layer layers)
                                                                                                                           ;; to match order in map-component
                                                                                                                           reverse)
                                                                                                                       [working-area-layer
                                                                                                                        route-layer
                                                                                                                        booking-layer])
                                                                                                               clj->js)
                                                                                            :view         ol-view
                                                                                            :interactions interactions})]
                                 (.setCenter ol-view (clj->js center))
                                 (reset! *map-atom {:map           ol-map
                                                    :gl-layer      gl-layer
                                                    :layers        layers
                                                    :bookings      bookings
                                                    :selected-ids  selected-ids
                                                    :route-ids     route-ids
                                                    :working-areas working-areas})
                                 (map-common-util/fit-map booking-src ol-view)))
       :component-did-update did-update
       :reagent-render       (fn [] [:div {:id id :class class :style style}])})))


(defn dispatch-map [municipality-ids bookings selected-booking-id]
  (let [bookings-with-location (filter (comp workspace-model/has-location booking-model/get-workspace) bookings)
        show-row-layers        (ff/enabled? :feature-flag.id/public-works-location-enhancements)
        layers                 (mapcat #(<sub [::municipality/municipal-layers % show-row-layers])
                                       municipality-ids)
        map-center             (<sub [::municipality/center-coordinates (first municipality-ids)])
        selected-inspector-id  (<sub [::dispatch-subs/selected-inspector-id])
        working-areas          (<sub [::area-feature-collection-with-selected])
        selected-ids           (cond
                                 selected-inspector-id
                                 (->> bookings-with-location
                                      (filter #(-> % :booking/assignee :db/id (= selected-inspector-id)))
                                      (map :db/id))

                                 selected-booking-id
                                 [selected-booking-id])
        route-ids              (if selected-inspector-id
                                 selected-ids
                                 [])]
    (when map-center
      [ol-map {:id            "dispatch-map"
               :class         "w-full h-full"
               :bookings      bookings-with-location
               :center        map-center
               :layers        layers
               :selected-ids  selected-ids
               :route-ids     route-ids
               :working-areas working-areas}])))
